import React, {useRef, useEffect, useMemo, useCallback} from 'react';
import {
    MDXEditor,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    thematicBreakPlugin,
    markdownShortcutPlugin,
    linkPlugin,
    linkDialogPlugin,
    imagePlugin,
    tablePlugin,
    codeBlockPlugin,
    codeMirrorPlugin,
    toolbarPlugin,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    UndoRedo,
    BoldItalicUnderlineToggles,
    StrikeThroughSupSubToggles,
    BlockTypeSelect,
    CreateLink,
    InsertImage,
    InsertTable,
    InsertCodeBlock,
    ListsToggle,
    Separator,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';

// MDXEditor wrapper. Same exported prop contract as the previous Toast-UI
// wrapper so the 7 call sites (markdown-block, field-name-description-editor,
// parameter-table x2, parameter-view, security-scheme-view, example-editor)
// require no changes.
//
// Prop contract:
//   value     : initial markdown string
//   onValue   : called with current markdown on blur
//   onChange  : called with current markdown on each change
//   height    : editor height in pixels (default 200)
//   onUpload  : async function (File) => imageUrl; enables the image toolbar

// Normalize legacy markdown so MDXEditor's Lexical-based parser can swallow it.
// Historical content produced by Toast-UI v2/v3 sometimes has:
//   - literal <br> or <br/> tags (instead of double newlines)
//   - bare <mark> from highlight buttons
//   - HTML comments, BOMs, etc.
// MDXEditor's default plugin set treats unknown JSX/HTML strictly; if parse
// fails the editor renders blank. This normalizer is defensive — converts
// the known-problematic patterns to markdown-safe equivalents.
function normalizeMarkdown(src) {
    if (typeof src !== 'string') return '';
    let md = src;
    // <br> / <br/> / <br /> → real newline (markdown equivalent)
    md = md.replace(/<br\s*\/?>/gi, '\n');
    // Strip zero-width + BOM characters that sometimes sneak in from paste
    md = md.replace(/[​﻿]/g, '');
    return md;
}

export function MarkdownEditor({value, onValue, onChange, height = 200, onUpload}) {
    const editorRef = useRef(null);

    // Normalize the incoming markdown ONCE at mount. If value changes later,
    // the editor keeps its current state (MDXEditor's markdown prop is consumed
    // at mount only, consistent with Toast-UI v2 wrapper behavior).
    const initialMarkdown = useMemo(() => normalizeMarkdown(value), []);
    // eslint-disable-line react-hooks/exhaustive-deps

    // Keep latest handlers in refs so the long-lived editor sees current
    // props without remounting on every render.
    const onValueRef = useRef(onValue);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onValueRef.current = onValue;
        onChangeRef.current = onChange;
    });

    // Fire onValue on unmount, mirroring old onBlur-on-destroy behavior.
    useEffect(() => {
        return () => {
            try {
                if (editorRef.current && onValueRef.current) {
                    const md = editorRef.current.getMarkdown();
                    onValueRef.current(md);
                }
            } catch (e) {
                // noop on unmount race
            }
        };
    }, []);

    // Stable reference for MDXEditor's onUpload binding. If we recreated
    // the wrapper on every render, MDXEditor's prop identity would shift
    // and (more importantly) plugins below would too — triggering an
    // internal editor remount each render. Combined with MDXEditor
    // firing onChange on its own initialization, that's an infinite
    // re-render loop visible to the user as a continuous layout shake.
    const imageUploadHandlerRef = useRef(null);
    imageUploadHandlerRef.current = onUpload;
    const imageUploadHandler = useMemo(
        () => (onUpload ? async (file) => imageUploadHandlerRef.current && imageUploadHandlerRef.current(file) : undefined),
        // We intentionally omit onUpload from deps — the ref above keeps
        // it current. We want this wrapper to live exactly as long as
        // the editor itself.
        [Boolean(onUpload)],
    );

    // Plugins must have a stable identity across renders. MDXEditor
    // treats the `plugins` prop as configuration that, when changed,
    // triggers a full editor reset — which fires onChange with the
    // initial markdown, which calls our setValue, which re-renders the
    // parent with a new plugins array. Memoize once.
    const plugins = useMemo(() => {
        const list = [
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            tablePlugin(),
            codeBlockPlugin({defaultCodeBlockLanguage: 'javascript'}),
            codeMirrorPlugin({
                codeBlockLanguages: {
                    js: 'JavaScript',
                    javascript: 'JavaScript',
                    ts: 'TypeScript',
                    typescript: 'TypeScript',
                    json: 'JSON',
                    yaml: 'YAML',
                    sh: 'Shell',
                    bash: 'Shell',
                    html: 'HTML',
                    css: 'CSS',
                    '': 'Text',
                },
            }),
            // Source-view toggle. Gives users (and us, debugging) a raw-
            // markdown fallback — if MDXEditor's visual parser ever
            // chokes on a spec's content, the source view still shows
            // what's there.
            diffSourcePlugin({diffMarkdown: initialMarkdown, viewMode: 'rich-text'}),
        ];

        if (imageUploadHandler) {
            list.push(imagePlugin({imageUploadHandler}));
        }

        list.push(
            toolbarPlugin({
                toolbarContents: () => (
                    <DiffSourceToggleWrapper options={['rich-text', 'source']}>
                        <UndoRedo />
                        <Separator />
                        <BoldItalicUnderlineToggles />
                        <StrikeThroughSupSubToggles options={['Strikethrough']} />
                        <Separator />
                        <BlockTypeSelect />
                        <Separator />
                        <CreateLink />
                        {imageUploadHandler && <InsertImage />}
                        <InsertTable />
                        <InsertCodeBlock />
                        <Separator />
                        <ListsToggle />
                    </DiffSourceToggleWrapper>
                ),
            }),
        );

        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMarkdown, imageUploadHandler]);

    // Stable handler identities so MDXEditor doesn't see prop churn.
    const handleChange = useCallback((md) => {
        if (onChangeRef.current) onChangeRef.current(md);
    }, []);
    const handleBlur = useCallback(() => {
        try {
            if (editorRef.current && onValueRef.current) {
                const md = editorRef.current.getMarkdown();
                onValueRef.current(md);
            }
        } catch (e) {
            // noop
        }
    }, []);
    const handleErrorCb = useCallback((err) => {
        console.warn('MDXEditor parse warning:', err && err.error);
    }, []);

    const wrapperStyle = useMemo(
        () => ({
            height: `${height}px`,
            overflowX: 'hidden',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: 4,
        }),
        [height],
    );

    return (
        <div className="markdownEditor" style={wrapperStyle}>
            <MDXEditor
                ref={editorRef}
                markdown={initialMarkdown}
                plugins={plugins}
                onChange={handleChange}
                onBlur={handleBlur}
                onError={handleErrorCb}
            />
        </div>
    );
}
